// src/components/shared/modals/AddCourseModal.tsx
// Modal for adding courses to a specific semester. Two modes: Course Search
// and Create Placeholder. Search mode is multi-select: picked courses collect
// in a "Selected" list, each with an optional grade and (for courses whose
// gen-ed is an OR, e.g. "DSHU or DSSP") a choice of area, and are added in one
// request. Built for onboarding, where a student enters every course from
// several past semesters.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Search, Plus, AlertCircle, Loader2, Bookmark, Check } from 'lucide-react';
import { Button, ModalShell, Select } from '../../ui';
import { useAddCourseSearch } from '../../../hooks/useAddCourseSearch';
import { useGradeOptions } from '../../../hooks/useGradeOptions';
import { useInstitutionConfig, useInstitutionTheme } from '../../../contexts/InstitutionConfigContext';
import { generatePlaceholderId } from '../../../hooks/academicPlan/helpers';
import type {
  AddCoursesItem,
  AddCoursesResult,
  GenEdOrGroup,
  GenEdPreviewCourse,
} from '../../../services/typedApiClient';

interface SearchResultCourse {
  id?: number;
  code: string;
  title: string;
  credits: number;
  offering_pattern?: Record<string, boolean>;
  gen_ed_requirements?: string[] | null;
}

/** A course in the Selected list. `genEdChoices` holds only the OR groups the
 *  student changed, keyed by group index; unchanged groups use the preview's
 *  default, which is what the backend stores when no choice is sent. */
interface SelectedCourse {
  course: SearchResultCourse;
  grade: string;
  genEdChoices: Record<number, string>;
  /** Set when the last add attempt failed for this course. */
  error?: string;
}

interface OfferingPattern {
  fall?: boolean;
  spring?: boolean;
  summer?: boolean;
  winter?: boolean;
}

const SemesterFilter = ({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) => {
  const [currentYear] = useState(new Date().getFullYear());

  const semesters = [
    { code: null, label: 'Any Semester' },
    { code: `${currentYear}08`, label: `Fall ${currentYear}` },
    { code: `${currentYear + 1}01`, label: `Spring ${currentYear + 1}` },
    { code: `${currentYear + 1}05`, label: `Summer ${currentYear + 1}` },
    { code: `${currentYear + 1}08`, label: `Fall ${currentYear + 1}` },
  ];

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-primary focus:border-transparent"
    >
      {semesters.map((sem) => (
        <option key={sem.code || 'any'} value={sem.code || ''}>
          {sem.label}
        </option>
      ))}
    </select>
  );
};

const PatternFilter = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const patterns = [
    { value: 'all', label: 'All Patterns' },
    { value: 'fall-only', label: 'Fall Only' },
    { value: 'spring-only', label: 'Spring Only' },
    { value: 'every-semester', label: 'Every Semester' },
  ];

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-primary focus:border-transparent"
    >
      {patterns.map((pattern) => (
        <option key={pattern.value} value={pattern.value}>
          {pattern.label}
        </option>
      ))}
    </select>
  );
};

const AvailabilityBadge = ({ pattern }: { pattern?: OfferingPattern }) => {
  const badges: Array<{ label: string; color: string }> = [];

  if (pattern) {
    if (pattern.fall) badges.push({ label: 'Fall', color: 'bg-orange-400/15 text-orange-700 dark:text-orange-300' });
    if (pattern.spring) badges.push({ label: 'Spring', color: 'bg-green-500/15 text-green-700 dark:text-green-300' });
    if (pattern.summer) badges.push({ label: 'Summer', color: 'bg-yellow-400/20 text-yellow-700 dark:text-yellow-300' });
    if (pattern.winter) badges.push({ label: 'Winter', color: 'bg-blue-400/15 text-blue-700 dark:text-blue-300' });
  }

  if (badges.length === 0) return null;

  return (
    <div className="flex gap-1 mt-1">
      {badges.map((badge, idx) => (
        <span key={idx} className={`text-xs px-1.5 py-0.5 rounded ${badge.color}`}>
          {badge.label}
        </span>
      ))}
    </div>
  );
};

interface PlaceholderModeData {
  title: string;
  credits: number;
  notes: string;
}

interface FormErrors {
  course?: string;
  title?: string;
  credits?: string;
  submit?: string;
  [key: string]: string | undefined;
}

export interface AddCourseModalCourseInput {
  code: string;
  title?: string;
  credits?: number;
  course_id?: number;
  isPlaceholder: boolean;
  notes?: string;
}

export interface AddCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Placeholder mode: adds the one placeholder. */
  onAddCourse: (course: AddCourseModalCourseInput, semesterCode: string) => Promise<void> | void;
  /** Search mode: adds every selected course. Resolves to the per-course
   *  result (null if nothing was sent); rejects if the request failed. */
  onAddCourses: (items: AddCoursesItem[], semesterCode: string) => Promise<AddCoursesResult | null>;
  /** Default gen-ed area for each OR group, given the courses in this order.
   *  Without it no area choice is offered and the backend default applies. */
  onPreviewGenEd?: (courseCodes: string[]) => Promise<GenEdPreviewCourse[]>;
  onAddToBookmarks?: (course: SearchResultCourse) => Promise<void> | void;
  targetSemester?: { code: string; name: string } | null;
  academicPlan?: unknown;
}

/** Matches an OR group in a course's gen_ed_requirements ("DSHUorDSSP",
 *  "DSNL(if taken with GEOL110)orDSNS"). Only used to decide whether to ask
 *  the backend for a preview; the backend does the real parsing. */
const OR_GROUP_RE = /[A-Za-z0-9)]or[A-Z0-9]/;

const hasOrGroup = (course: SearchResultCourse): boolean =>
  (course.gen_ed_requirements ?? []).some((r) => typeof r === 'string' && OR_GROUP_RE.test(r));

const SelectedCourseRow = ({
  item,
  orGroups,
  gradeOptions,
  areaName,
  inPlan,
  isBookmarked,
  disabled,
  onGradeChange,
  onGenEdChange,
  onBookmark,
  onRemove,
}: {
  item: SelectedCourse;
  orGroups: GenEdOrGroup[];
  gradeOptions: Array<{ value: string; label: string }>;
  areaName: (code: string) => string;
  inPlan: boolean;
  isBookmarked: boolean;
  disabled: boolean;
  onGradeChange: (grade: string) => void;
  onGenEdChange: (groupIndex: number, area: string, isDefault: boolean) => void;
  onBookmark?: () => void;
  onRemove: () => void;
}) => {
  const { course } = item;
  return (
    <li className="px-3 py-2.5 border-b border-line-soft last:border-b-0">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">{course.code}</span>
            <span className="text-sm text-ink-3">{course.credits} cr</span>
            {inPlan && (
              <span className="inline-flex items-center gap-1 text-xs text-status-warning-fg">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                Already in your plan
              </span>
            )}
          </div>
          <p className="text-sm text-ink-2 truncate">{course.title}</p>
        </div>
        {onBookmark && (
          <Button
            intent="ghost"
            size="sm"
            onClick={onBookmark}
            disabled={isBookmarked || disabled}
            aria-label={isBookmarked ? `${course.code} is bookmarked` : `Bookmark ${course.code}`}
            title={isBookmarked ? 'Bookmarked' : 'Bookmark'}
            icon={<Bookmark className={`h-4 w-4 ${isBookmarked ? 'text-status-warning fill-current' : ''}`} />}
          />
        )}
        <Button
          intent="ghost"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${course.code}`}
          title="Remove"
          icon={<X className="h-4 w-4" />}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Width comes from a wrapper: Select is w-full by design, and `cn`
            joins rather than merges classes, so a conflicting w-/h-/py-
            override on the Select itself loses to stylesheet order (that is
            what clipped the text in the first staging build). */}
        <label className="flex items-center gap-2 text-xs text-ink-2">
          Grade
          <span className="inline-block w-28">
          <Select
            value={item.grade}
            onChange={(e) => onGradeChange(e.target.value)}
            disabled={disabled}
            aria-label={`Grade for ${course.code}`}
          >
            {gradeOptions.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.value ? option.label : 'No grade'}
              </option>
            ))}
          </Select>
          </span>
        </label>
        {orGroups.map((group, index) => {
          const value = item.genEdChoices[index] ?? group.default;
          return (
            <label key={group.options.join('|')} className="flex items-center gap-2 text-xs text-ink-2">
              Counts toward
              <span className="inline-block w-80 max-w-full">
              <Select
                value={value}
                onChange={(e) => onGenEdChange(index, e.target.value, e.target.value === group.default)}
                disabled={disabled}
                aria-label={`Gen ed area for ${course.code}`}
              >
                {group.options.map((area) => (
                  <option key={area} value={area}>
                    {areaName(area)}{area === group.default ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
              </span>
            </label>
          );
        })}
      </div>

      {item.error && (
        <p className="mt-2 text-sm text-status-critical-fg" role="alert">{item.error}</p>
      )}
    </li>
  );
};

const AddCourseModal = ({
  isOpen,
  onClose,
  onAddCourse,
  onAddCourses,
  onPreviewGenEd,
  onAddToBookmarks,
  targetSemester,
  academicPlan,
}: AddCourseModalProps) => {
  const [mode, setMode] = useState<'search' | 'placeholder'>('search');
  const [selected, setSelected] = useState<SelectedCourse[]>([]);
  const [genEdPreview, setGenEdPreview] = useState<Record<string, GenEdOrGroup[]>>({});
  // Phase 6.5: per-institution example codes for placeholder text.
  // Phase G: neutral fallbacks ('Course Code', 'Upper Level Course')
  // replace the previous UMD-specific 'CMSC131'/'CMSC498'. Every
  // shipped theme (UMD, SJSU) populates exampleCourseCodes, so the
  // fallback only fires for a future misconfigured theme — better to
  // surface "no example available" than show the wrong institution's
  // codes.
  const exampleCourseCodes = useInstitutionTheme().exampleCourseCodes;
  const primaryExample = exampleCourseCodes[0] ?? 'Course Code';
  const upperLevelExample = exampleCourseCodes[1] ?? 'Upper Level Course';
  const gradeOptions = useGradeOptions();
  const genEdAreas = useInstitutionConfig()?.config?.genEdAreas;
  const areaName = useCallback((code: string): string => {
    const name = genEdAreas?.find((a) => a.code === code)?.name;
    return name ? `${code} · ${name}` : code;
  }, [genEdAreas]);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    showFilters,
    setShowFilters,
    semesterFilter,
    setSemesterFilter,
    patternFilter,
    setPatternFilter,
    hasActiveFilters,
    clearFilters,
    isCourseInPlan,
    isCourseBookmarked,
  } = useAddCourseSearch({ isOpen, mode, academicPlan: academicPlan as never });

  const [placeholderData, setPlaceholderData] = useState<PlaceholderModeData>({
    title: '',
    credits: 3,
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setMode('search');
      setSelected([]);
      setGenEdPreview({});
      setPlaceholderData({ title: '', credits: 3, notes: '' });
      setErrors({});
    }
  }, [isOpen]);

  // Ask the backend for each OR group's default whenever the selection
  // changes. Order matters (an earlier course's area shifts a later one's
  // default), so the whole ordered list is sent. A stale response is dropped.
  const previewKey = selected
    .filter((s) => hasOrGroup(s.course))
    .length > 0
    ? selected.map((s) => s.course.code).join(',')
    : '';
  const previewRequest = useRef(0);
  useEffect(() => {
    if (!previewKey || !onPreviewGenEd) {
      setGenEdPreview({});
      return;
    }
    const requestId = ++previewRequest.current;
    onPreviewGenEd(previewKey.split(','))
      .then((courses) => {
        if (requestId !== previewRequest.current) return;
        const next: Record<string, GenEdOrGroup[]> = {};
        for (const c of courses) next[c.course_code] = c.or_groups;
        setGenEdPreview(next);
      })
      .catch(() => {
        // No preview means no area choice; the backend default still applies.
        if (requestId === previewRequest.current) setGenEdPreview({});
      });
  }, [previewKey, onPreviewGenEd]);

  const selectedCodes = useMemo(() => new Set(selected.map((s) => s.course.code)), [selected]);
  const selectedCredits = selected.reduce((sum, s) => sum + (Number(s.course.credits) || 0), 0);

  const toggleCourse = (course: SearchResultCourse) => {
    setErrors({});
    setSelected((prev) =>
      prev.some((s) => s.course.code === course.code)
        ? prev.filter((s) => s.course.code !== course.code)
        : [...prev, { course, grade: '', genEdChoices: {} }],
    );
  };

  // Picking a result (click or Enter) clears the search and returns focus to
  // it, so the next code can be typed straight away.
  // Blocked while a submit is in flight: the result handler rebuilds the list
  // from the selection as it was when the request went out, so a pick made in
  // that window would be silently dropped.
  const pickCourse = (course: SearchResultCourse) => {
    if (submitting) return;
    if (!selectedCodes.has(course.code)) {
      toggleCourse(course);
      setSearchQuery('');
      searchInputRef.current?.focus();
    } else {
      toggleCourse(course);
    }
  };

  const updateSelected = (code: string, patch: Partial<SelectedCourse>) => {
    setSelected((prev) => prev.map((s) => (s.course.code === code ? { ...s, ...patch, error: undefined } : s)));
  };

  const setGenEdChoice = (code: string, groupIndex: number, area: string, isDefault: boolean) => {
    setSelected((prev) => prev.map((s) => {
      if (s.course.code !== code) return s;
      const genEdChoices = { ...s.genEdChoices };
      // Choosing the default again clears the choice, so the course stays
      // on the backend's default (and the resolver may still revise it).
      if (isDefault) delete genEdChoices[groupIndex];
      else genEdChoices[groupIndex] = area;
      return { ...s, genEdChoices, error: undefined };
    }));
  };

  // Enter picks the first result not already selected and clears the box,
  // so a student can type a code, press Enter, and type the next one.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = searchResults.find((c) => !selectedCodes.has(c.code));
    if (next) pickCourse(next);
  };

  const handlePlaceholderInputChange = (field: keyof PlaceholderModeData, value: string | number) => {
    setPlaceholderData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validatePlaceholder = (): boolean => {
    const newErrors: FormErrors = {};
    if (!placeholderData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (placeholderData.credits < 0 || placeholderData.credits > 6) {
      newErrors.credits = 'Credits must be between 0 and 6';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addSelectedCourses = async (): Promise<void> => {
    if (!targetSemester || selected.length === 0) return;

    const items: AddCoursesItem[] = selected.map((s) => {
      const groups = genEdPreview[s.course.code] ?? [];
      const choices = Object.entries(s.genEdChoices)
        .filter(([index, area]) => groups[Number(index)] && area !== groups[Number(index)].default)
        .map(([, area]) => area);
      return {
        course_code: s.course.code,
        grade: s.grade || null,
        gen_ed_choices: choices,
      };
    });

    const result = await onAddCourses(items, targetSemester.code);
    if (!result) return;
    if (result.failed === 0) {
      onClose();
      return;
    }
    // Keep only what failed, each with its reason, so the student can fix or
    // remove it. The courses that went in are already on the plan. Results
    // come back one per item in request order, so match by position: the
    // backend normalizes codes, and a code-keyed match could miss.
    const sent = selected;
    setSelected(sent.flatMap((s, i) => {
      const r = result.results[i];
      return r && r.status === 'failed'
        ? [{ ...s, error: r.message || 'Could not add this course' }]
        : [];
    }));
  };

  const addPlaceholder = async (): Promise<void> => {
    if (!validatePlaceholder() || !targetSemester) return;
    await onAddCourse({
      // The 'placeholder-*' namespace is load-bearing (#205): the backend
      // validates it on add and routes move/remove by it. The old
      // title-derived code (e.g. 'SCIENCEELECTIVE') was tolerated when
      // placeholders were client-only; it is rejected by persistence —
      // this was the staging-rehearsal 400.
      code: generatePlaceholderId(),
      title: placeholderData.title,
      credits: placeholderData.credits,
      notes: placeholderData.notes,
      isPlaceholder: true,
    }, targetSemester.code);
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setErrors({});
    try {
      if (mode === 'search') await addSelectedCourses();
      else await addPlaceholder();
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: (error as { message?: string })?.message || 'Failed to add course' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddToBookmarks = async (course: SearchResultCourse): Promise<void> => {
    if (!onAddToBookmarks) return;
    try {
      await onAddToBookmarks(course);
    } catch (error) {
      console.error('Bookmark error:', error);
      setErrors({ submit: (error as { message?: string })?.message || 'Failed to bookmark course' });
    }
  };

  if (!isOpen) return null;

  const semesterName = targetSemester?.name || 'Semester';
  let submitLabel = `Add to ${semesterName}`;
  if (mode === 'search' && selected.length === 1) {
    submitLabel = `Add ${selected[0].course.code} to ${semesterName}`;
  } else if (mode === 'search' && selected.length > 1) {
    submitLabel = `Add ${selected.length} courses to ${semesterName}`;
  }

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel="Add courses"
      maxWidth="max-w-[calc(100vw-1rem)] sm:max-w-2xl"
      className="max-h-[90vh] overflow-hidden"
    >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Add Courses to {semesterName}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 hover:bg-surface-sunken rounded-control transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <button
              onClick={() => setMode('search')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                mode === 'search'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Search Courses
            </button>
            <button
              onClick={() => setMode('placeholder')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                mode === 'placeholder'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Create Placeholder
            </button>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-14rem)]">
          {mode === 'search' ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="add-course-search" className="block text-sm font-medium text-gray-700">
                    Search for courses
                  </label>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`text-xs px-2 py-1 rounded-md transition-colors ${
                      showFilters
                        ? 'bg-primary/10 text-primary-700 dark:text-primary-300'
                        : 'text-ink-2 hover:bg-surface-sunken'
                    }`}
                  >
                    Filters{hasActiveFilters && ' *'}
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="add-course-search"
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    disabled={submitting}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={`e.g., ${primaryExample} or Introduction to Programming`}
                    autoFocus
                  />
                  {/* Centered with auto margins, not -translate-y-1/2: the
                      `spin` keyframes replace the element transform, which
                      would drop the centering for as long as it spins. */}
                  {searchLoading && (
                    <Loader2 className="absolute right-3 inset-y-0 my-auto h-4 w-4 text-gray-400 animate-spin" />
                  )}
                </div>
                <p className="mt-1 text-xs text-ink-3">
                  Pick a course to add it, then search for the next. Enter picks the top result.
                </p>
              </div>

              {showFilters && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Search Filters</span>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Semester</label>
                      <SemesterFilter value={semesterFilter} onChange={setSemesterFilter} />
                    </div>

                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Offering Pattern</label>
                      <PatternFilter value={patternFilter} onChange={setPatternFilter} />
                    </div>
                  </div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                  {searchResults.map((course) => {
                    const inPlan = isCourseInPlan(course.code);
                    const isBookmarked = isCourseBookmarked(course.code);

                    const isSelected = selectedCodes.has(course.code);
                    return (
                      <button
                        key={course.code || course.id}
                        onClick={() => pickCourse(course)}
                        disabled={submitting}
                        aria-pressed={isSelected}
                        className={`w-full px-3 py-2 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                          isSelected
                            ? 'bg-primary/15 ring-1 ring-inset ring-primary/40'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{course.code}</span>
                              <span className="text-sm text-gray-500">{course.credits} cr</span>
                              {inPlan && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded-full">
                                  In Plan
                                </span>
                              )}
                              {isBookmarked && (
                                <Bookmark className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 truncate">{course.title}</p>
                            <AvailabilityBadge pattern={course.offering_pattern} />
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 shrink-0 text-primary mt-1" aria-hidden="true" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">No courses found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setMode('placeholder')}
                    className="mt-2 text-sm text-slate-600 hover:text-slate-700 hover:underline"
                  >
                    Create a placeholder instead
                  </button>
                </div>
              )}

              {selected.length > 0 && (
                <section aria-label="Selected courses">
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="text-sm font-medium text-ink">
                      Selected ({selected.length})
                    </h3>
                    <span className="text-xs text-ink-3">{selectedCredits} credits</span>
                  </div>
                  <ul className="border border-line-soft rounded-card">
                    {selected.map((item) => (
                      <SelectedCourseRow
                        key={item.course.code}
                        item={item}
                        orGroups={genEdPreview[item.course.code] ?? []}
                        gradeOptions={gradeOptions}
                        areaName={areaName}
                        inPlan={isCourseInPlan(item.course.code)}
                        isBookmarked={isCourseBookmarked(item.course.code)}
                        disabled={submitting}
                        onGradeChange={(grade) => updateSelected(item.course.code, { grade })}
                        onGenEdChange={(index, area, isDefault) =>
                          setGenEdChoice(item.course.code, index, area, isDefault)}
                        onBookmark={onAddToBookmarks ? () => handleAddToBookmarks(item.course) : undefined}
                        onRemove={() => toggleCourse(item.course)}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Create a placeholder for a course you plan to take but can't find in the catalog.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={placeholderData.title}
                  onChange={(e) => handlePlaceholderInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={`e.g., Technical Elective or ${upperLevelExample}`}
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credits</label>
                <input
                  type="number"
                  value={placeholderData.credits}
                  onChange={(e) =>
                    handlePlaceholderInputChange('credits', parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  max="6"
                  step="0.1"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.credits ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.credits && <p className="mt-1 text-sm text-red-600">{errors.credits}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={placeholderData.notes}
                  onChange={(e) => handlePlaceholderInputChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Any notes about this course..."
                />
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="mt-4 p-3 bg-status-critical/10 border border-status-critical/25 rounded-card">
              <p className="text-sm text-status-critical-fg">{errors.submit}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-line-soft flex justify-end gap-3">
          <Button intent="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={submitting || (mode === 'search' && selected.length === 0)}
            icon={<Plus className="h-4 w-4" />}
          >
            {submitLabel}
          </Button>
        </div>
    </ModalShell>
  );
};

export default AddCourseModal;
